/**
 * Per-asset inspection questions.
 * Packs come from this building's copy. Edits stay on the asset.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../utils/api';
import InspectionQuestionEditor from './InspectionQuestionEditor';

function packCodes(sets) {
  return (sets || []).map((set) => String(set.code || '').toUpperCase()).filter(Boolean);
}

export default function InspectionAssetChecklist({
  buildingId,
  moduleCode,
  asset,
  onClose,
  onSaved,
  orderKind = 'INSPECTION',
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['building', buildingId, 'inspection-checklist', moduleCode, orderKind],
    queryFn: async () =>
      (
        await api.get(`/inspection-modules/${moduleCode}/buildings/${buildingId}`, {
          params: orderKind === 'CALIBRATION' ? { order: 'CALIBRATION' } : undefined,
        })
      ).data?.data,
    enabled: !!buildingId && !!moduleCode,
  });

  const sets = useMemo(
    () => (Array.isArray(data?.checklist_sets) ? data.checklist_sets : []),
    [data]
  );
  const codes = useMemo(() => packCodes(sets), [sets]);

  const packField = orderKind === 'CALIBRATION' ? 'checklist_cal_packs' : 'checklist_packs';
  const itemField = orderKind === 'CALIBRATION' ? 'checklist_cal_items' : 'checklist_items';
  const [packs, setPacks] = useState(() =>
    Array.isArray(asset?.[packField]) ? asset[packField].map((code) => String(code).toUpperCase()) : null
  );
  const [snapshots, setSnapshots] = useState(() =>
    asset?.[itemField] && typeof asset[itemField] === 'object' ? asset[itemField] : {}
  );
  const [active, setActive] = useState('');
  const [savingCode, setSavingCode] = useState('');
  const [savingPacks, setSavingPacks] = useState(false);

  useEffect(() => {
    if (!codes.length) return;
    setPacks((current) => (Array.isArray(current) ? current.filter((code) => codes.includes(code)) : [...codes]));
  }, [codes]);

  const applied = Array.isArray(packs) ? packs : codes;
  const visibleSets = sets.filter((set) => applied.includes(String(set.code).toUpperCase()));

  useEffect(() => {
    if (!visibleSets.length) {
      setActive('');
      return;
    }
    if (!visibleSets.some((set) => String(set.code).toUpperCase() === active)) {
      setActive(String(visibleSets[0].code).toUpperCase());
    }
  }, [visibleSets, active]);

  const activeSet = visibleSets.find((set) => String(set.code).toUpperCase() === active) || null;
  const editorItems = activeSet
    ? Array.isArray(snapshots[activeSet.code])
      ? snapshots[activeSet.code]
      : activeSet.items || []
    : [];

  async function savePacks(next) {
    setSavingPacks(true);
    try {
      const row = (
        await api.patch(
          `/inspection-modules/${moduleCode}/buildings/${buildingId}/assets/${asset.id}`,
          { checklist_packs: next, order_kind: orderKind }
        )
      ).data?.data;
      setPacks(Array.isArray(row?.[packField]) ? row[packField] : next);
      onSaved?.(row);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save packs');
    } finally {
      setSavingPacks(false);
    }
  }

  function togglePack(code) {
    const key = String(code).toUpperCase();
    const current = Array.isArray(packs) ? packs : [...codes];
    const next = current.includes(key) ? current.filter((item) => item !== key) : [...current, key];
    setPacks(next);
    savePacks(next);
  }

  async function saveItems(code, pack) {
    const key = String(code).toUpperCase();
    const nextItems = { ...(snapshots || {}), [key]: pack.items || [] };
    setSavingCode(key);
    try {
      const row = (
        await api.patch(
          `/inspection-modules/${moduleCode}/buildings/${buildingId}/assets/${asset.id}`,
          {
            checklist_packs: applied,
            checklist_items: nextItems,
            order_kind: orderKind,
          }
        )
      ).data?.data;
      setSnapshots(row?.[itemField] || nextItems);
      onSaved?.(row);
      toast.success('Saved for this unit');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save questions');
    } finally {
      setSavingCode('');
    }
  }

  const label = asset?.asset_tag || asset?.name || 'Unit';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl my-6">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b">
          <div>
            <h3 className="font-semibold text-gray-900">{label}</h3>
            <p className="text-xs text-gray-500 mt-1">
              Questions for this unit. Saving here does not change the building checklist or other units.
            </p>
          </div>
          <button type="button" className="text-sm text-gray-600 hover:text-gray-900" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="p-4 space-y-4">
          {isLoading && <p className="text-sm text-gray-500">Loading packs…</p>}
          {!isLoading && !sets.length && (
            <p className="text-sm text-gray-600">No checklist packs on this building yet. Use Edit checklist first.</p>
          )}
          {!!sets.length && (
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">Packs that apply</p>
              <div className="flex flex-wrap gap-2">
                {sets.map((set) => {
                  const code = String(set.code).toUpperCase();
                  const on = applied.includes(code);
                  return (
                    <label
                      key={code}
                      className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm ${
                        on ? 'border-primary-600 bg-primary-50' : 'bg-white'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={savingPacks}
                        onChange={() => togglePack(code)}
                      />
                      <span>
                        <span className="font-semibold">{code}</span>
                        {set.name ? <span className="text-gray-600"> · {set.name}</span> : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {!!sets.length && !visibleSets.length && (
            <p className="text-sm text-gray-600">No packs apply to this unit.</p>
          )}

          {visibleSets.length > 1 && (
            <div className="flex flex-wrap gap-1">
              {visibleSets.map((set) => {
                const code = String(set.code).toUpperCase();
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setActive(code)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${
                      active === code ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700'
                    }`}
                  >
                    {code}
                  </button>
                );
              })}
            </div>
          )}

          {activeSet && (
            <InspectionQuestionEditor
              key={`${asset.id}-${activeSet.code}`}
              pack={{ ...activeSet, items: editorItems }}
              saving={savingCode === String(activeSet.code).toUpperCase()}
              onSave={saveItems}
            />
          )}
        </div>
      </div>
    </div>
  );
}
